import type { Card } from '../types';

export class Deck {
  private deck: Card[] = [];
  private discard: Card[] = [];
  private exhaust: Card[] = [];
  private cardIdCounter: number = 0;
  
  constructor(cards: Card[]) {
    // Expand cards based on copies
    this.deck = [];
    for (const card of cards) {
      const copies = card.copies || 1;
      for (let i = 0; i < copies; i++) {
        // Create a copy of the card object with unique ID
        const cardCopy = { ...card, id: `card_${this.cardIdCounter++}` };
        this.deck.push(cardCopy);
      }
    }
    this.shuffle();
  }
  
  shuffle(): void {
    // Fisher-Yates shuffle
    for (let i = this.deck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.deck[i], this.deck[j]] = [this.deck[j], this.deck[i]];
    }
  }
  
  getTopCards(count: number): Card[] {
    return this.deck.slice(0, Math.min(count, this.deck.length));
  }
  
  getTopCard(): Card | null {
    return this.deck.length > 0 ? this.deck[0] : null;
  }
  
  playCard(index: number): Card | null {
    if (index < 0 || index >= this.deck.length) {
      return null;
    }
    const card = this.deck.splice(index, 1)[0];
    return card;
  }
  
  discardCard(card: Card): void {
    this.discard.push(card);
  }
  
  exhaustCard(card: Card): void {
    this.exhaust.push(card);
  }
  
  addToTop(card: Card): void {
    this.deck.unshift(card);
  }
  
  insertAt(index: number, card: Card): void {
    if (index < 0 || index > this.deck.length) {
      // If invalid index, add to top
      this.deck.unshift(card);
      return;
    }
    this.deck.splice(index, 0, card);
  }
  
  // Time cost: discard from top of deck (excluding current card)
  // This means we discard cards starting from index 1 (after the current card)
  discardFromTop(count: number): Card[] {
    if (count <= 0) return [];
    
    // NOTE: When this is called, the current card has already been removed from the deck
    // So position 0 is the first visible card (was position 1 before)
    // We discard from the visible hand first (positions 0-3), then from unrevealed deck
    
    if (this.deck.length < count) {
      return []; // Not enough cards to discard
    }
    
    const toDiscard: Card[] = [];
    // Discard from visible hand first (positions 0-3, which are the top 4 cards)
    const visibleHandSize = Math.min(4, this.deck.length);
    const toDiscardFromHand = Math.min(count, visibleHandSize);
    
    if (toDiscardFromHand > 0) {
      // Discard from position 0 onwards (first visible card)
      const discarded = this.deck.splice(0, toDiscardFromHand);
      toDiscard.push(...discarded);
    }
    
    // If we still need more, discard from unrevealed deck (position 0, which is now the new top after hand cards)
    const remaining = count - toDiscard.length;
    if (remaining > 0) {
      const discarded = this.deck.splice(0, remaining);
      toDiscard.push(...discarded);
    }
    
    this.discard.push(...toDiscard);
    return toDiscard;
  }
  
  getDeckSize(): number {
    return this.deck.length;
  }
  
  getDiscardSize(): number {
    return this.discard.length;
  }
  
  isEmpty(): boolean {
    return this.deck.length === 0;
  }
  
  // Shuffle discard into deck
  reshuffleDiscard(): void {
    this.deck.push(...this.discard);
    this.discard = [];
    this.shuffle();
  }
  
  getDiscard(): Card[] {
    return [...this.discard];
  }
  
  getExhaust(): Card[] {
    return [...this.exhaust];
  }
  
  // Complete reset - clear everything and reinitialize with new cards
  reset(cards: Card[]): void {
    this.deck = [];
    this.discard = [];
    this.exhaust = [];
    this.cardIdCounter = 0;
    
    // Reinitialize deck from cards
    for (const card of cards) {
      const copies = card.copies || 1;
      for (let i = 0; i < copies; i++) {
        // Create a copy of the card object with unique ID
        const cardCopy = { ...card, id: `card_${this.cardIdCounter++}` };
        this.deck.push(cardCopy);
      }
    }
    this.shuffle();
  }
}

